import axios, { AxiosResponse } from 'axios';
import { Store } from '@reduxjs/toolkit';
import { RootState, AppDispatch } from '../store/store';
import { tokenRefreshed, logout } from '../store/slices/authSlice';
import { env } from '../config/env';

const api = axios.create({
  baseURL: env.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Aynı anda giden aynı GET isteklerini tek ağ isteğinde birleştir. Birden fazla bileşen aynı veriyi
// istediğinde (ve geliştirmede React StrictMode effect'leri iki kez çalıştırdığında) istekler
// ikişer kez gidiyordu. Sadece süren istekler paylaşılır; yanıt geldikten sonra önbellek tutulmaz.
const sendRequest = axios.getAdapter(axios.defaults.adapter);
const inFlightGets = new Map<string, Promise<AxiosResponse>>();

api.defaults.adapter = (config) => {
  if ((config.method ?? 'get').toLowerCase() !== 'get') {
    return sendRequest(config);
  }
  const key = [
    config.baseURL,
    config.url,
    JSON.stringify(config.params ?? {}),
    config.headers?.Authorization ?? '',
  ].join('|');
  const pending = inFlightGets.get(key);
  if (pending) {
    return pending;
  }
  const request = sendRequest(config).finally(() => inFlightGets.delete(key));
  inFlightGets.set(key, request);
  return request;
};

export const setupInterceptors = (store: Store<RootState> & { dispatch: AppDispatch }) => {
  // Request interceptor
  api.interceptors.request.use(
    (config) => {
      const token = store.getState().auth.token;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const refreshToken = localStorage.getItem('refresh');
          if (!refreshToken) {
            store.dispatch(logout());
            return Promise.reject(error);
          }
          
          const response = await axios.post(`${api.defaults.baseURL}/auth/token/refresh/`, {
            refresh: refreshToken,
          });
          
          const { access } = response.data;

          localStorage.setItem('token', access);
          store.dispatch(tokenRefreshed(access));

          originalRequest.headers.Authorization = `Bearer ${access}`;
          return api(originalRequest);
        } catch (refreshError) {
          store.dispatch(logout());
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};

export default api;
